import { useState, useEffect } from 'react';
import { supabase } from '../supabase/supabase';
import { weeklyLineupUtils } from '../utils/weeklyLineupUtils';
import { getCached, setCached, invalidateLeague, LIVE_TTL_MS } from '../utils/queryCache';

export const useWeeklyLineup = ({ leagueId, memberId, currentWeek }) => {
  const [selectedWeek, setSelectedWeek] = useState(currentWeek);
  const [weeklyLineups, setWeeklyLineups] = useState({});
  const [weekStatuses, setWeekStatuses] = useState({});
  const [loading, setLoading] = useState(true);
  const [availableWeeks, setAvailableWeeks] = useState([]);
  const [hasTripPlay, setHasTripPlay] = useState(false);
  const [tripPlayUsedWeek, setTripPlayUsedWeek] = useState(null);
  const [freezesRemaining, setFreezesRemaining] = useState(0);

  useEffect(() => {
    initializeWeeklyLineups();
  }, [leagueId, memberId, currentWeek]); // eslint-disable-line react-hooks/exhaustive-deps

  const initializeWeeklyLineups = async () => {
    if (!leagueId || !memberId) {
      setLoading(false);
      return;
    }
    try {
      const weeks = Array.from({ length: 14 }, (_, i) => i + 1);
      setAvailableWeeks(weeks);

      const cacheKey = `weeklyLineup:${leagueId}:${memberId}:week:${currentWeek}`;
      const cached = getCached(cacheKey);
      if (cached) {
        setHasTripPlay(cached.hasTripPlay);
        setTripPlayUsedWeek(cached.tripPlayUsedWeek);
        setFreezesRemaining(cached.freezesRemaining);
        setWeeklyLineups(cached.lineupData);
        calculateWeekStatuses(weeks);
        setLoading(false);
        return;
      }

      // Fetch member status fields + all weekly lineup rows in parallel
      const [{ data: memberData }, { data: weeklyRows }] = await Promise.all([
        supabase
          .from('league_members')
          .select('has_trip_play, trip_play_used_week, freezes_remaining')
          .eq('id', memberId)
          .single(),
        supabase
          .from('weekly_lineups')
          .select('*')
          .eq('league_id', leagueId)
          .eq('member_id', memberId),
      ]);

      if (memberData) {
        setHasTripPlay(memberData.has_trip_play || false);
        setTripPlayUsedWeek(memberData.trip_play_used_week || null);
        setFreezesRemaining(memberData.freezes_remaining ?? 0);
      }

      const historicalData = {};
      (weeklyRows || []).forEach(row => {
        historicalData[`week${row.week}`] = row;
      });

      const lineupData = {};
      weeks.forEach(week => {
        const weekKey = `week${week}`;
        const histRow = historicalData[weekKey];

        if (histRow) {
          lineupData[weekKey] = {
            starters: histRow.starters || Array(5).fill(null),
            bench: histRow.bench || Array(2).fill(null),
            captain: histRow.captain || null,
            tripPlayTeam: histRow.trip_play_team || null,
            frozenTeams: histRow.frozen_teams || [],
            // Per-team earned points as the scorer wrote them. A frozen team's
            // entry is the score it was frozen at, multiplier included, and
            // is the only place that score exists: its live weekly points keep
            // moving after the freeze.
            teamPoints: histRow.team_points || {},
            lockedTeams: [],
            teamLockTimes: {},
            lockedAt: null,
            // The week's own total, written by the scorer. Null until the week
            // has been scored, which is not the same as zero: an empty lineup
            // really does score zero, and the two must not read alike.
            points: histRow.points ?? null,
            isEditable: week === currentWeek
          };
        } else {
          // No row yet for this week. A roster carries forward, so fall back to
          // the most recent week that does have one rather than showing an
          // empty lineup. backend/lineups.js writes the real row each morning;
          // this covers the window between the week rolling over and that job
          // running, which is otherwise a manager opening the app to find their
          // five teams gone.
          //
          // Display only, and only up to the current week: nothing is written
          // here, and saving upserts the full lineup anyway.
          const carried = week <= currentWeek
            ? Object.keys(historicalData)
                .map((k) => historicalData[k])
                .filter((r) => r.week < week)
                .sort((a, b) => b.week - a.week)[0]
            : null;

          lineupData[weekKey] = {
            starters: carried?.starters || Array(5).fill(null),
            bench: carried?.bench || Array(2).fill(null),
            captain: null,
            tripPlayTeam: null,
            frozenTeams: [],
            lockedTeams: [],
            teamLockTimes: {},
            lockedAt: null,
            // A carried roster is a display fallback for a week with no row of
            // its own, so it has no score. It must not inherit the score of the
            // week it was carried from.
            points: null,
            isEditable: week === currentWeek
          };
        }
      });

      setWeeklyLineups(lineupData);
      calculateWeekStatuses(weeks);
      setLoading(false);

      // Always the live interval: weekly_lineups.points is rewritten by score
      // ingestion, and this hook has no game rows of its own to tell whether
      // anything is in progress. Every write below invalidates the entry, so
      // a manager's own edits appear immediately regardless.
      setCached(
        cacheKey,
        {
          hasTripPlay: memberData?.has_trip_play || false,
          tripPlayUsedWeek: memberData?.trip_play_used_week || null,
          freezesRemaining: memberData?.freezes_remaining ?? 0,
          lineupData,
        },
        LIVE_TTL_MS
      );
    } catch (error) {
      console.error("Error initializing weekly lineups:", error);
      setLoading(false);
    }
  };

  const calculateWeekStatuses = (weeks) => {
    const statuses = {};
    for (const week of weeks) {
      let status = 'future';
      if (week < currentWeek) status = 'completed';
      else if (week === currentWeek) status = 'editable';
      statuses[week] = {
        status,
        lockTime: null,
        unlockTime: null,
        firstGameTime: null,
        lastGameTime: null,
        allGamesComplete: week < currentWeek,
        hasLiveGames: false
      };
    }
    setWeekStatuses(statuses);
  };

  const saveLineup = async (week, starters, bench, captain = null, tripPlayTeam = null) => {
    try {
      if (week !== currentWeek) {
        console.warn(`Cannot save Week ${week}. Only current week (${currentWeek}) can be edited.`);
        return;
      }

      const normalizedStarters = starters.map(team => team ? weeklyLineupUtils.normalizeTeamName(team) : null);
      const normalizedBench = bench.map(team => team ? weeklyLineupUtils.normalizeTeamName(team) : null);

      const weekKey = `week${week}`;
      const currentTripPlayTeam = weeklyLineups[weekKey]?.tripPlayTeam || null;

      // Upsert lineup row in weekly_lineups
      const { error: upsertError } = await supabase
        .from('weekly_lineups')
        .upsert({
          league_id: leagueId,
          member_id: memberId,
          week,
          starters: normalizedStarters,
          bench: normalizedBench,
          captain,
          trip_play_team: tripPlayTeam,
        }, { onConflict: 'league_id,member_id,week' });

      if (upsertError) throw upsertError;

      // Handle trip play state changes on league_members
      let memberPatch = null;
      if (tripPlayTeam && hasTripPlay) {
        memberPatch = { has_trip_play: false, trip_play_used_week: week };
        setHasTripPlay(false);
        setTripPlayUsedWeek(week);
      } else if (!tripPlayTeam && currentTripPlayTeam && tripPlayUsedWeek === week) {
        memberPatch = { has_trip_play: true, trip_play_used_week: null };
        setHasTripPlay(true);
        setTripPlayUsedWeek(null);
      } else if (!tripPlayTeam && currentTripPlayTeam && tripPlayUsedWeek !== week) {
        console.warn("Cannot remove trip play - it was used in a different week");
      }

      if (memberPatch) {
        const { error: memberErr } = await supabase
          .from('league_members')
          .update(memberPatch)
          .eq('id', memberId);
        if (memberErr) throw memberErr;
      }

      setWeeklyLineups(prev => ({
        ...prev,
        [weekKey]: {
          ...prev[weekKey],
          starters: normalizedStarters,
          bench: normalizedBench,
          captain,
          tripPlayTeam,
        }
      }));

      // The League and Scoreboard tabs read captain, 5x and points off the
      // same rows, so every cache for this league has to go, not just this
      // hook's.
      invalidateLeague(leagueId);
    } catch (error) {
      console.error("Error saving lineup:", error);
      throw error;
    }
  };

  // snapshot is the game at that moment (utils/freezeDetail.js), stored with
  // the activity event so the frozen row can later show what it was frozen at.
  const saveFreezePlay = async (week, normalizedTeamName, frozenPoints, snapshot = null) => {
    if (week !== currentWeek) return;

    const weekKey = `week${week}`;
    const existing = weeklyLineups[weekKey] || {};
    if ((existing.frozenTeams || []).includes(normalizedTeamName)) return;

    try {
      const { data: row } = await supabase
        .from('weekly_lineups')
        .select('team_points, frozen_teams')
        .eq('league_id', leagueId)
        .eq('member_id', memberId)
        .eq('week', week)
        .maybeSingle();

      const newFrozen     = [...(row?.frozen_teams || []), normalizedTeamName];
      const newTeamPoints = { ...(row?.team_points || {}), [normalizedTeamName]: frozenPoints };

      // An UPDATE, not an upsert. .upsert() is INSERT ... ON CONFLICT, and an
      // INSERT that omits starters, bench and captain carries their defaults
      // ('{}' and NULL). enforce_lineup_lock (migration 060) diffs that against
      // the stored row, reads every starter and the captain as removed, and
      // rejects the write because they have kicked off, which by the time a
      // freeze is allowed they always have. An UPDATE leaves those columns as
      // they are, so the trigger sees nothing moved. The row always exists
      // here: only a starter in a saved lineup can be frozen.
      const { data: updated, error: lineupErr } = await supabase
        .from('weekly_lineups')
        .update({
          frozen_teams: newFrozen,
          team_points:  newTeamPoints,
        })
        .eq('league_id', leagueId)
        .eq('member_id', memberId)
        .eq('week', week)
        .select('id');

      if (lineupErr) throw lineupErr;
      if (!updated?.length) throw new Error('No lineup found for this week to freeze.');

      const newRemaining = Math.max(0, freezesRemaining - 1);
      const { error: memberErr } = await supabase
        .from('league_members')
        .update({ freezes_remaining: newRemaining })
        .eq('id', memberId);

      if (memberErr) throw memberErr;

      setFreezesRemaining(newRemaining);
      setWeeklyLineups(prev => ({
        ...prev,
        [weekKey]: { ...prev[weekKey], frozenTeams: newFrozen, teamPoints: newTeamPoints },
      }));

      // A freeze earns a line in the recent activity feed. It is written here,
      // at the moment it happens, rather than deferred like the 5x Play:
      // freezing is irreversible and only allowed from the second half, so the
      // game is already underway and nothing about the manager's intent is
      // given away by publishing it.
      //
      // 23505 is the unique index from migration 042 rejecting a repeat. That
      // is the intended outcome of a retry, not a failure worth surfacing.
      const { error: eventErr } = await supabase.from('move_history').insert({
        league_id: leagueId,
        member_id: memberId,
        week,
        event_type: 'freeze',
        team_added: normalizedTeamName,
        detail: { ...(snapshot || {}), points: frozenPoints },
      });
      if (eventErr && eventErr.code !== '23505') {
        console.error('Freeze saved, but recording it in recent activity failed:', eventErr);
      }

      // A freeze is irreversible and decrements freezes_remaining, which the
      // League tab shows too.
      invalidateLeague(leagueId);
    } catch (error) {
      console.error('Error saving freeze play:', error);
      throw error;
    }
  };

  const getTripPlayStatusMessage = () => {
    if (hasTripPlay) return "5X PLAY AVAILABLE";
    if (tripPlayUsedWeek) return `5X PLAY USED (Week ${tripPlayUsedWeek})`;
    return "5X USED";
  };

  const getWeekStatus = (week) => weekStatuses[week]?.status;

  const getStatusMessage = (week) => {
    const status = weekStatuses[week];
    if (!status) return "Loading...";
    switch (status.status) {
      case 'editable':       return 'Teams lock 1 hour before their games start';
      case 'live':           return 'Games are live! Check your scores';
      case 'locked_playing': return 'Games in progress - individual teams may be locked';
      case 'completed':      return 'Week completed';
      case 'future':         return 'Future week';
      default:               return 'Individual teams lock based on game times';
    }
  };

  const formatDateTime = (date) =>
    new Date(date).toLocaleString('en-US', {
      month: 'short', day: 'numeric',
      hour: 'numeric', minute: '2-digit',
      timeZoneName: 'short'
    });

  return {
    selectedWeek,
    setSelectedWeek,
    weeklyLineups,
    weekStatuses,
    loading,
    availableWeeks,
    hasTripPlay,
    tripPlayUsedWeek,
    freezesRemaining,
    saveLineup,
    saveFreezePlay,
    getTripPlayStatusMessage,
    getWeekStatus,
    getStatusMessage,
    formatDateTime,
    // Re-read the lineup rows, bypassing the cache. The week total on the
    // header is written by the scorer, which now moves while a game is
    // running, so the live poll in WeeklyLineupManager needs a way to pull it
    // again without remounting the page.
    refresh: () => {
      invalidateLeague(leagueId);
      return initializeWeeklyLineups();
    },
  };
};
